import dayjs, { Dayjs } from 'dayjs';
import { EventApi, EventInput } from '@fullcalendar/core';
import {
  PortalBgType,
  PortalBorderType,
  PortalTextType,
  TaskDTO,
  ThemeHexColorSchemas,
} from '@nx-frontend/shared/types';
import { POPOVER_WIDTH } from './more-tasks-popover';
import { TasksFiltersState, TasksRequestQueryFilters } from './types';

const CALENDAR_USER_VARS_KEY = 'saved-task-list-calendar';

export function getTimeGridMoreLinkDateTime(el: HTMLElement): string | null {
  if (el.classList.contains('fc-timegrid-more-link')) {
    const { top, bottom } = (el.querySelector('.fc-timegrid-more-link-inner') as HTMLElement).getBoundingClientRect();
    const date = (el.closest('td.fc-timegrid-col') as HTMLElement).getAttribute('data-date');
    const slot = Array.from(
      (el.closest('div.fc-timegrid-body') as HTMLElement).querySelectorAll(
        '.fc-timegrid-slots td.fc-timegrid-slot.fc-timegrid-slot-label'
      )
    ).find((slotEl) => {
      const { top: slotTop, bottom: slotBottom } = slotEl.getBoundingClientRect();
      return top > slotTop && bottom < slotBottom;
    });
    if (slot) {
      const time = slot.getAttribute('data-time');
      if (date) {
        if (time) {
          return date + ' ' + time;
        }
        return date;
      }
    }
  }
  return null;
}

export function getMoreLinkOverlapEvents(el: HTMLElement) {
  const moreLinkTime = `${new Date(el.getAttribute('data-date-time') as string).getTime()}`;
  return Array.from((el.closest('.fc-timegrid-col-events') as HTMLElement).querySelectorAll('span.calendar-event'))
    .filter((taskEl) => {
      return moreLinkTime === taskEl.getAttribute('data-date');
    })
    .map((taskEl) => taskEl.closest('.fc-timegrid-event-harness') as HTMLElement);
}

export function getMoreItemsPopoverPos(target: HTMLElement, container: HTMLElement) {
  let x = 0;
  let y = 0;
  let el = target;
  while (el !== container && el !== null) {
    x += el.offsetLeft - el.scrollLeft;
    y += el.offsetTop - el.scrollTop;
    el = el.offsetParent as HTMLElement;
  }
  const targetWidth = target.getBoundingClientRect().width;
  if (targetWidth > POPOVER_WIDTH) {
    x += (targetWidth - POPOVER_WIDTH) / 2;
  } else {
    x -= (POPOVER_WIDTH - targetWidth) / 2;
  }
  return { x, y };
}

export function saveUserTasksVars(
  taskListId: string,
  { calendarView, blockView }: { calendarView?: string; blockView?: string }
) {
  const savedProps = localStorage.getItem(CALENDAR_USER_VARS_KEY);
  const saveProps: Record<string, Record<string, string>> = savedProps ? { ...JSON.parse(savedProps) } : {};
  if (!saveProps[taskListId]) {
    saveProps[taskListId] = {};
  }
  if (calendarView) {
    saveProps[taskListId].calendarView = calendarView;
  }
  if (blockView) {
    saveProps[taskListId].blockView = blockView;
  }
  localStorage.setItem(CALENDAR_USER_VARS_KEY, JSON.stringify(saveProps));
}

export function getUserTasksVars(taskListId: string): Record<string, string> | null {
  const savedProps = localStorage.getItem(CALENDAR_USER_VARS_KEY);
  const parsedProps: Record<string, Record<string, string>> = savedProps ? JSON.parse(savedProps) : {};
  return parsedProps[taskListId] || null;
}

/**
 * Returns the date with the precision of 30 minutes from the given time in seconds
 * @param time
 * @returns
 */
function convertToDateWith30MinPrecision(time: number, normalize: boolean) {
  const dateParts = dayjs(time * (normalize ? 1000 : 1))
    .format('YYYY-MM-DD HH:mm')
    .split(' ');
  const [hourString, minutesString] = dateParts[1].split(':');
  const minutes = parseInt(minutesString);
  const roundedDateString = dateParts[0] + ' ' + hourString + ':' + (minutes < 30 ? '00' : '30') + ':00';
  return dayjs(roundedDateString).utc().local().format();
}

function convertToDateWithoutTime(time: number, normalize: boolean) {
  return convertToDate(time, normalize).replace(/T.*$/, '');
}

function convertToDate(time: number, normalize: boolean) {
  return dayjs(time * (normalize ? 1000 : 1))
    .utc()
    .local()
    .format();
}

export function getTaskStartAndEndDates(showTime: boolean, deadlineDate: number | null | undefined, normalize = true) {
  // get dates with the precision of 30 minutes
  let start: string | undefined = undefined;
  let end: string | undefined = undefined;
  if (deadlineDate) {
    start = showTime
      ? convertToDateWith30MinPrecision(deadlineDate, normalize)
      : convertToDateWithoutTime(deadlineDate, normalize);
    end = showTime ? convertToDate(deadlineDate + 1, normalize) : undefined;
  }
  return {
    start,
    end,
  };
}

export function getDayTasks(date: Date, tasks: EventApi[]) {
  return tasks.filter((task) => {
    return dayjs(task.start).isSame(date, 'day');
  });
}

export function getMonthWithMoreDaysInWeek(startDate: Date, endDate: Date, format = 'MMMM') {
  function countDaysInRange(start: Dayjs, end: Dayjs) {
    return end.diff(start, 'day') + 1;
  }

  const dStartDate = dayjs(startDate);
  const dEndDate = dayjs(endDate);

  if (dStartDate.month() === dEndDate.month()) {
    return dStartDate.format(format);
  } else {
    const daysInFirstMonth = countDaysInRange(dStartDate, dStartDate.endOf('month'));
    const daysInSecondMonth = countDaysInRange(dEndDate.startOf('month'), dEndDate);

    if (daysInFirstMonth > daysInSecondMonth) {
      return dStartDate.format(format);
    } else if (daysInSecondMonth > daysInFirstMonth) {
      return dEndDate.format(format);
    } else {
      return dStartDate.format(format);
    }
  }
}

export function mapToCalendarTasks(tasks: TaskDTO[]): EventInput[] {
  return tasks
    .filter((task) => task.deadlineDate !== null)
    .reduce((result: EventInput[], task: TaskDTO) => {
      if (!result.find((t: EventInput) => t.id === task.globalId)) {
        const { start, end } = getTaskStartAndEndDates(task.showTime, task.deadlineDate);
        result.push({
          id: task.globalId,
          title: task.title,
          start,
          end,
          done: task.done,
          className: task.done ? 'completed-task' : '',
          extendedProps: {
            originalTime: task.deadlineDate ? task.deadlineDate * 1000 : null,
            taskListId: task.taskListId,
            workspaceId: task.workspaceId,
          },
        } as EventInput);
      }
      return result;
    }, []);
}

export function mapFiltersToRequestQueryFilters(filters: TasksFiltersState): TasksRequestQueryFilters {
  const result = {} as TasksRequestQueryFilters;
  for (const key in filters) {
    if (key === 'labels' && filters.labels.length) {
      result.labelKeys = filters.labels.map((label) => label.workspaceId + '#' + label.labelId);
    } else if (key === 'assigneeIds' && filters.assigneeIds.length) {
      result.assigneeUserIds = filters.assigneeIds.map((id) => parseInt(id)).map((id) => (isNaN(id) ? 0 : id));
    } else if (key === 'date' && filters.date.length) {
      result.deadlines = [
        ...filters.date
          .map((value) => {
            if (value === 'noDates') {
              return 'noDeadline';
            }
            if (value === 'twoDaysDue') {
              return 'expiresIn-2d';
            }
            if (value === 'overdue') {
              return 'expired';
            }
            return '';
          })
          .filter((value) => value !== ''),
      ];
    } else if (key === 'workspaceIds') {
      result.workspaceIds = [...filters.workspaceIds];
    }
  }

  return result;
}

export function getThemeCssVars(theme: ThemeHexColorSchemas) {
  const colorVars = {
    '--fc-small-font-size': '0.85em',
    '--fc-event-resizer-thickness': '8px',
    '--fc-event-resizer-dot-total-width': '8px',
    '--fc-event-resizer-dot-border-width': '1px',
    '--fc-bg-event-opacity': '0.3',
    '--fc-page-bg-color': 'transparent',
    '--fc-border-color': getHexColor(theme, PortalBorderType.SURFACE_INNER),
    '--fc-button-text-color': getHexColor(theme, PortalTextType.PRIMARY),
    '--fc-button-bg-color': 'transparent',
    '--fc-button-border-color': getHexColor(theme, PortalBorderType.SURFACE_INNER),
    '--fc-button-hover-bg-color': getHexColor(theme, PortalBorderType.SURFACE_INNER),
    '--fc-button-hover-border-color': getHexColor(theme, PortalBorderType.SURFACE_INNER),
    '--fc-button-active-bg-color': getHexColor(theme, PortalBgType.SURFACE_INNER_HOVER),
    '--fc-button-active-border-color': getHexColor(theme, PortalBorderType.SURFACE_VARIANT_HOVER),
    '--fc-button-active-text-color': getHexColor(theme, PortalTextType.SURFACE_VARIANT_2),
    '--fc-event-bg-color': getHexColor(theme, PortalBgType.SURFACE),
    '--fc-event-border-color': getHexColor(theme, PortalBorderType.SURFACE_INNER),
    '--fc-event-text-color': getHexColor(theme, PortalTextType.SURFACE),
    '--fc-time-grid-more-text-color': getHexColor(theme, PortalTextType.PRIMARY),
    '--fc-event-hover-bg-color': getHexColor(theme, PortalBgType.SURFACE_INNER_VARIANT),
    '--fc-more-link-bg-color': 'transparent',
    '--fc-more-link-text-color': getHexColor(theme, PortalTextType.SURFACE_3),
    '--fc-past-event-text-color': getHexColor(theme, PortalTextType.SURFACE_3),
    '--fc-bg-event-color': getHexColor(theme, PortalBgType.SURFACE),
    '--fc-highlight-color': getHexColor(theme, PortalBgType.SURFACE_INNER_VARIANT),
    '--fc-day-text-color': getHexColor(theme, PortalTextType.SURFACE_INNER_2),
    '--fc-day-past-text-color': getHexColor(theme, PortalTextType.SURFACE_3),
    '--fc-today-bg-color': getHexColor(theme, PortalBgType.SURFACE),
    '--fc-today-number-bg-color': '#F43F5E',
    '--fc-today-number-text-color': getHexColor(theme, PortalTextType.SECONDARY),
    '--fc-now-indicator-color': '#ef4444',
    '--fc-weekend-bg-color': getHexColor(theme, PortalBgType.SURFACE_DISABLE),
    '--fc-time-grid-week-today-color': '#6ee7b726',
  };
  return `:root {${Object.keys(colorVars)
    .map((varKey) => varKey + ':' + colorVars[varKey as keyof typeof colorVars])
    .join(';')}}`;
}

export function getHexColor(themeColors: ThemeHexColorSchemas, type: PortalBgType | PortalBorderType | PortalTextType) {
  const directColor = typeof type !== 'string';

  return themeColors.reduce((hex: string, { colors }) => {
    hex = directColor ? type : colors[type][0];
    return hex;
  }, '');
}
